import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MarkdownService } from '../../shared/services/markdown.service';
import { AgentAskResponse, AoaiService } from '../../shared/services/aoai.service';

type Role = 'user' | 'assistant';
interface Msg {
  role: Role;
  markdown: string;
  html: SafeHtml;
  pending?: boolean;
  id?: string;
}

@Component({
  selector: 'app-prompts',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './prompts.component.html',
  styleUrls: ['./prompts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromptsComponent implements AfterViewInit {
  @ViewChild('promptInput') promptInput!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('scrollArea') scrollArea!: ElementRef<HTMLDivElement>;

  draft = '';
  loading = false;
  messages: Msg[] = [];
  showTopShadow = false;
  private threadId: string | null = localStorage.getItem('cw.threadId');

  constructor(
    private md: MarkdownService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
    private aoai: AoaiService
  ) {}

  ngAfterViewInit() {
    this.autoGrow(this.promptInput.nativeElement);
    this.observeScrollShadow();
  }

  private observeScrollShadow() {
    const el = this.scrollArea.nativeElement;
    const onScroll = () => {
      this.showTopShadow = el.scrollTop + el.clientHeight < el.scrollHeight;
      this.cdr.markForCheck();
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  autoGrow(el: HTMLTextAreaElement) {
    el.style.height = 'auto';
    const max = 160; // ~6 lines
    el.style.height = Math.min(el.scrollHeight, max) + 'px';
  }

  onKeyDown(ev: KeyboardEvent) {
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();
      this.send();
    }
  }

  send() {
    const text = this.draft.trim();
    if (!text || this.loading) return;

    // push user message
    this.messages.push({ role: 'user', markdown: text, html: this.safeHtml(text)});
    this.draft = '';
    this.autoGrow(this.promptInput.nativeElement);
    this.scrollToBottom();

    // create a pending assistant bubble
    const pending: Msg = { role: 'assistant', markdown: '', html: this.safeHtml(''), pending: true, id: crypto.randomUUID() };
    this.messages.push(pending);
    this.cdr.markForCheck();

    // stream from backend
    this.askAssistantOnce(text, pending);
  }

  private askAssistantOnce(userText: string, pending: Msg) {
    this.loading = true;

    this.aoai.ask({ message: userText, threadId: this.threadId || undefined }).subscribe({
      next: (resp: AgentAskResponse) => {
        this.loading = false;

        // keep thread continuity
        this.threadId = resp.threadId ?? this.threadId;
        if (this.threadId) localStorage.setItem('cw.threadId', this.threadId);

        // take the latest assistant message from the array
        const lastAssistant =
          [...resp.messages].reverse().find(m => m.role === 'assistant')?.text ?? '(no reply)';

        // replace pending bubble with rendered markdown
        this.updateMsg(pending, lastAssistant, false);
        pending.pending = false;
        this.cdr.markForCheck();
        this.decorateCodeBlocksSoon();
      },
      error: (err: any) => {
        this.loading = false;
        const msg = (err?.error?.message || err?.message || 'Something went wrong').toString();
        this.updateMsg(pending, `**Error:** ${msg}`, false);
      },
    });
  }


  /** Use streaming endpoint; falls back to non-stream if not available */
  private streamAssistant(userText: string, pending: Msg) {
    this.loading = true;
    let acc = '';
    let threadCaptured = false;

    this.aoai
      .askStream({ message: userText, threadId: this.threadId || undefined })
      .subscribe({
        next: (ev : any) => {
          if (ev.type === 'thread' && ev.threadId && !threadCaptured) {
            threadCaptured = true;
            this.threadId = ev.threadId;
            localStorage.setItem('cw.threadId', this.threadId ?? '');
          }
          if (ev.type === 'delta' && ev.delta) {
            acc += ev.delta;
            this.updateMsg(pending, acc, true);
          }
          if (ev.type === 'done') {
            pending.pending = false;
            this.loading = false;
            this.cdr.markForCheck();
            this.decorateCodeBlocksSoon();
          }
        },
        error: (_ : any) => {
          // fallback to one-shot call
          this.aoai.ask({ message: userText, threadId: this.threadId || undefined }).subscribe({
            next: (resp: AgentAskResponse) => {
              this.loading = false;
              this.threadId = resp.threadId ?? this.threadId;
              if (this.threadId) localStorage.setItem('cw.threadId', this.threadId);

              const lastAssistant =
                [...resp.messages].reverse().find(m => (m.role as string).toLowerCase() === 'assistant')?.text ??
                '(no reply)';

              this.updateMsg(pending, lastAssistant, false);
              pending.pending = false;
              this.cdr.markForCheck();
              this.decorateCodeBlocksSoon();
            },
            error: (err: any) => {
              this.loading = false;
              const msg = (err?.error?.message || err?.message || 'Something went wrong').toString();
              this.updateMsg(pending, `**Error:** ${msg}`, false);
            },
          });
        },
      });
  }

  private safeHtml(md: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(md);
  }

  private updateMsg(msg: Msg, md: string, keepPending: boolean) {
    msg.markdown = md;
    msg.html = this.safeHtml(md);
    msg.pending = keepPending;
    this.cdr.markForCheck();
    this.scrollToBottom();
  }

  private decorateCodeBlocksSoon() {
    this.zone.runOutsideAngular(() =>
      setTimeout(() => {
        const container = this.scrollArea.nativeElement;
        container.querySelectorAll('pre').forEach(pre => {
          if (pre.querySelector('.copy-btn')) return;
          const btn = document.createElement('button');
          btn.className = 'copy-btn';
          btn.textContent = 'Copy';
          btn.addEventListener('click', () => {
            const code = pre.querySelector('code')?.textContent ?? '';
            navigator.clipboard.writeText(code);
            btn.textContent = 'Copied!';
            setTimeout(() => (btn.textContent = 'Copy'), 1200);
          });
          pre.appendChild(btn);
        });
      }, 0)
    );
  }

  private scrollToBottom() {
    this.zone.runOutsideAngular(() => {
      const el = this.scrollArea.nativeElement;
      requestAnimationFrame(() => el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' }));
    });
  }

  @HostListener('window:keydown', ['$event'])
  onGlobalKeydown(ev: KeyboardEvent) {
    if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === 'k') {
      ev.preventDefault();
      this.promptInput.nativeElement.focus();
    }
  }
}
